import { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { FaTimes, FaTrash, FaEdit, FaArchive, FaBolt, FaExclamationTriangle } from "react-icons/fa";

import Button from "./Button";
import { HeroIconByName } from "./HeroIconByName";

const GET_ITEM = gql`
  query GetSalesFunnelItem($id: ID!) {
    salesFunnelItem(id: $id) {
      id
      title
      position
      archived
      createdAt
      updatedAt
      stage {
        id
        name
        color
      }
      author {
        id
        name
        picture
      }
      fieldValues {
        id
        value
        fieldDefinition {
          id
          label
          type
          icon
          options
        }
      }
      assignedUsers {
        id
        user {
          id
          name
          picture
        }
        role
      }
      history {
        id
        changeType
        previousValue
        newValue
        createdAt
        author {
          id
          name
          picture
        }
        fieldDefinition {
          id
          label
        }
      }
      comments {
        id
        text
        createdAt
        author {
          id
          name
          picture
        }
      }
    }
    
    users {
      id
      name
      picture
    }
  }
`;

const UPDATE_ITEM = gql`
  mutation UpdateSalesFunnelItem($id: ID!, $title: String, $archived: Boolean) {
    updateSalesFunnelItem(id: $id, title: $title, archived: $archived) {
      success
      message
    }
  }
`;

const UPDATE_FIELD_VALUE = gql`
  mutation UpdateSalesFunnelItemFieldValue($itemId: ID!, $fieldDefinitionId: ID!, $value: JSON) {
    updateSalesFunnelItemFieldValue(itemId: $itemId, fieldDefinitionId: $fieldDefinitionId, value: $value) {
      success
      message
    }
  }
`;

const ADD_USER = gql`
  mutation AddSalesFunnelItemUser($itemId: ID!, $userId: ID!, $role: String) {
    addSalesFunnelItemUser(itemId: $itemId, userId: $userId, role: $role) {
      success
      message
    }
  }
`;

const REMOVE_USER = gql`
  mutation RemoveSalesFunnelItemUser($itemId: ID!, $userId: ID!) {
    removeSalesFunnelItemUser(itemId: $itemId, userId: $userId) {
      success
      message
    }
  }
`;

const ADD_COMMENT = gql`
  mutation AddSalesFunnelItemComment($itemId: ID!, $text: String!) {
    addSalesFunnelItemComment(itemId: $itemId, text: $text) {
      success 
      message
    }
  }
`;

const GET_BOARDS_FOR_LINKING = gql`
  query GetBoardsForLinking {
    salesFunnelBoards {
      id
      name
      items {
        id
        title
      }
    }
  }
`;

const GET_ITEM_LINKS = gql`
  query GetSalesFunnelItemLinks($itemId: ID!) {
    salesFunnelItemLinks(itemId: $itemId) {
      parentLinks {
        id
        parentItem {
          id
          title
          board {
            id
            name
          }
          stage {
            id
            name
            color
          }
        }
        automation {
          id
          name
        }
      }
      childLinks {
        id
        childItem {
          id
          title
          board {
            id
            name
          }
          stage {
            id
            name
            color
          }
        }
        automation {
          id
          name
        }
      }
    }
  }
`;

const DELETE_COMMENT = gql`
  mutation DeleteSalesFunnelItemComment($id: ID!) {
    deleteSalesFunnelItemComment(id: $id) {
      success
      message
    }
  }
`;

interface Props {
  itemId: string;
  boardId: string;
  fieldDefinitions: any[];
  onClose: () => void;
  onUpdate: () => void;
}

export default function SalesFunnelItemDetailModal({
  itemId,
  boardId,
  fieldDefinitions,
  onClose,
  onUpdate,
}: Props) {
  const { data, loading, refetch } = useQuery(GET_ITEM, {
    variables: { id: itemId },
  });

  const { data: boardsData } = useQuery(GET_BOARDS_FOR_LINKING);
  const { data: linksData, refetch: refetchLinks } = useQuery(GET_ITEM_LINKS, {
    variables: { itemId },
    skip: !itemId,
  });

  const [updateItem] = useMutation(UPDATE_ITEM);
  const [updateFieldValue] = useMutation(UPDATE_FIELD_VALUE);
  const [addUser] = useMutation(ADD_USER);
  const [removeUser] = useMutation(REMOVE_USER);
  const [addComment] = useMutation(ADD_COMMENT);
  const [deleteComment] = useMutation(DELETE_COMMENT);

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState("");
  const [newComment, setNewComment] = useState("");
  const [activeTab, setActiveTab] = useState<"details" | "timeline" | "comments">("details");
  const [timelineExpanded, setTimelineExpanded] = useState(false);

  const item = data?.salesFunnelItem;
  const users = data?.users || [];
  const allBoards = boardsData?.salesFunnelBoards || [];

  const handleUpdateTitle = async () => {
    if (!titleValue.trim()) return;
    try {
      await updateItem({ variables: { id: itemId, title: titleValue } });
      setEditingTitle(false);
      refetch();
      onUpdate();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleUpdateFieldValue = async (fieldDefinitionId: string, value: any) => {
    try {
      await updateFieldValue({
        variables: { itemId, fieldDefinitionId, value },
      });
      refetch();
      onUpdate();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleAddUser = async (userId: string) => {
    try {
      await addUser({ variables: { itemId, userId, role: "assignee" } });
      refetch();
      onUpdate();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    try {
      await removeUser({ variables: { itemId, userId } });
      refetch();
      onUpdate();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await addComment({ variables: { itemId, text: newComment } });
      setNewComment("");
      refetch();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await deleteComment({ variables: { id: commentId } });
      refetch();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleArchive = async () => {
    const action = item?.archived ? "unarchive" : "archive";
    if (!window.confirm(`Are you sure you want to ${action} this item?`)) return;
    try {
      await updateItem({ variables: { id: itemId, archived: !item?.archived } });
      refetch();
      onUpdate();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const getFieldValue = (fieldDefId: string) => {
    const fv = item?.fieldValues?.find((f: any) => f.fieldDefinition.id === fieldDefId);
    return fv?.value;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">Loading...</div>
      </div>
    );
  }

  if (!item) {
    return null;
  }

  const assignedUserIds = item.assignedUsers?.map((au: any) => au.user.id) || [];
  const availableUsers = users.filter((u: any) => !assignedUserIds.includes(u.id));

  return (
    <div
      className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: item.stage?.color || "#gray" }}
            />
            <span className="text-sm text-gray-500">{item.stage?.name}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleArchive}
              className="text-gray-400 hover:text-gray-600 p-2"
              title={item.archived ? "Unarchive" : "Archive"}
            >
              <FaArchive />
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2">
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="p-4 border-b">
          {editingTitle ? (
            <div className="flex items-center space-x-2">
              <input
                type="text"
                className="flex-1 text-xl font-semibold border rounded px-2 py-1"
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdateTitle();
                  if (e.key === "Escape") setEditingTitle(false);
                }}
                autoFocus
              />
              <Button sizeVariant="small" onClick={handleUpdateTitle}>
                Save
              </Button>
              <Button sizeVariant="small" variant="outlined" onClick={() => setEditingTitle(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <h2
              className="text-xl font-semibold cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 flex items-center group"
              onClick={() => {
                setTitleValue(item.title);
                setEditingTitle(true);
              }}
            >
              {item.title}
              <FaEdit className="ml-2 text-gray-300 opacity-0 group-hover:opacity-100" />
            </h2>
          )}
          {item.archived && (
            <span className="inline-block mt-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
              Archived
            </span>
          )}
        </div>

        <div className="border-b">
          <div className="flex">
            {(["details", "timeline", "comments"] as const).map((tab) => (
              <button
                key={tab}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === tab
                    ? "border-b-2 border-primary text-primary"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === "details" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Assigned Users</h3>
                <div className="flex flex-wrap gap-2">
                  {item.assignedUsers?.map((au: any) => (
                    <div
                      key={au.id}
                      className="flex items-center space-x-2 bg-gray-100 rounded-full px-3 py-1"
                    >
                      <img
                        src={au.user.picture || "/dummy-picture.jpg"}
                        alt={au.user.name}
                        className="w-6 h-6 rounded-full"
                      />
                      <span className="text-sm">{au.user.name}</span>
                      <button
                        onClick={() => handleRemoveUser(au.user.id)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <FaTimes className="text-xs" />
                      </button>
                    </div>
                  ))}
                  {availableUsers.length > 0 && (
                    <select
                      className="border rounded px-2 py-1 text-sm"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) handleAddUser(e.target.value);
                      }}
                    >
                      <option value="">+ Add user</option>
                      {availableUsers.map((u: any) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Fields</h3>
                <div className="space-y-4">
                  {[...(fieldDefinitions || [])]
                    .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
                    .map((field: any) => (
                    <FieldInput
                      key={field.id}
                      field={field}
                      value={getFieldValue(field.id)}
                      onChange={(value) => handleUpdateFieldValue(field.id, value)}
                      allBoards={allBoards}
                    />
                  ))}
                </div>
              </div>

              {/* Linked Cards Section */}
              {linksData?.salesFunnelItemLinks && (
                <div className="border-t pt-4">
                  {/* Cards linking to this card (parent links) */}
                  {linksData.salesFunnelItemLinks.parentLinks?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <span>🔗 Linked from</span>
                        <span className="text-xs text-gray-400">({linksData.salesFunnelItemLinks.parentLinks.length})</span>
                      </h4>
                      <div className="space-y-2">
                        {linksData.salesFunnelItemLinks.parentLinks.map((link: any) => (
                          <a
                            key={link.id}
                            href={`/funnel/${link.parentItem.board.id}?item=${link.parentItem.id}`}
                            className="flex items-center gap-2 p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                            onClick={(e) => {
                              e.preventDefault();
                              onClose();
                              window.location.href = `/funnel/${link.parentItem.board.id}?item=${link.parentItem.id}`;
                            }}
                          >
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: link.parentItem.stage?.color || '#ccc' }}
                            />
                            <span className="text-sm font-medium text-gray-800 truncate">
                              {link.parentItem.title}
                            </span>
                            <span className="text-xs text-gray-500">
                              in {link.parentItem.board.name}
                            </span>
                            {link.automation && (
                              <span className="text-xs text-yellow-600 ml-auto flex items-center gap-1">
                                <FaBolt className="text-xs" />
                                Auto
                              </span>
                            )}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Cards linked from this card (child links) */}
                  {linksData.salesFunnelItemLinks.childLinks?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <span>🔗 Links to</span>
                        <span className="text-xs text-gray-400">({linksData.salesFunnelItemLinks.childLinks.length})</span>
                      </h4>
                      <div className="space-y-2">
                        {linksData.salesFunnelItemLinks.childLinks.map((link: any) => (
                          <a
                            key={link.id}
                            href={`/funnel/${link.childItem.board.id}?item=${link.childItem.id}`}
                            className="flex items-center gap-2 p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                            onClick={(e) => {
                              e.preventDefault();
                              onClose();
                              window.location.href = `/funnel/${link.childItem.board.id}?item=${link.childItem.id}`;
                            }}
                          >
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: link.childItem.stage?.color || '#ccc' }}
                            />
                            <span className="text-sm font-medium text-gray-800 truncate">
                              {link.childItem.title}
                            </span>
                            <span className="text-xs text-gray-500">
                              in {link.childItem.board.name}
                            </span>
                            {link.automation && (
                              <span className="text-xs text-yellow-600 ml-auto flex items-center gap-1">
                                <FaBolt className="text-xs" />
                                Auto
                              </span>
                            )}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="text-xs text-gray-400 pt-4 border-t">
                <p>Created by {item.author?.name} on {new Date(item.createdAt).toLocaleString()}</p>
                <p>Last updated: {new Date(item.updatedAt).toLocaleString()}</p>
              </div>
            </div>
          )}

          {activeTab === "timeline" && (
            <div className="space-y-4">
              {item.history?.length === 0 && (
                <p className="text-gray-500 text-center py-4">No history yet</p>
              )}
              {(() => {
                const history = item.history || [];
                const total = history.length;
                const shouldCollapse = total > 10 && !timelineExpanded;
                const oldestEntries = shouldCollapse ? history.slice(0, 2) : [];
                const middleEntries = shouldCollapse ? history.slice(2, -5) : [];
                const latestEntries = shouldCollapse ? history.slice(-5) : history;
                const hiddenCount = middleEntries.length;

                const renderEntry = (entry: any) => (
                  <div key={entry.id} className="flex space-x-3">
                    <img
                      src={entry.author?.picture || "/dummy-picture.jpg"}
                      alt={entry.author?.name}
                      className="w-8 h-8 rounded-full"
                    />
                    <div className="flex-1">
                      <p className="text-sm">
                        <span className="font-medium">{entry.author?.name}</span>{" "}
                        {formatHistoryEntry(entry)}
                      </p>
                      <p className="text-xs text-gray-400">
                        {new Date(entry.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                );

                return (
                  <>
                    {shouldCollapse && oldestEntries.map(renderEntry)}
                    {shouldCollapse && hiddenCount > 0 && (
                      <button
                        onClick={() => setTimelineExpanded(true)}
                        className="flex items-center justify-center py-2 w-full hover:bg-gray-50 rounded transition-colors"
                      >
                        <div className="flex-1 border-t border-gray-200"></div>
                        <span className="px-3 text-xs text-blue-500 hover:text-blue-700 cursor-pointer">
                          Show {hiddenCount} more event{hiddenCount > 1 ? "s" : ""}
                        </span>
                        <div className="flex-1 border-t border-gray-200"></div>
                      </button>
                    )}
                    {latestEntries.map(renderEntry)}
                  </>
                );
              })()}
            </div>
          )}

          {activeTab === "comments" && (
            <div className="space-y-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  className="flex-1 border rounded px-3 py-2"
                  placeholder="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddComment();
                  }}
                />
                <Button onClick={handleAddComment}>Post</Button>
              </div>

              {item.comments?.length === 0 && (
                <p className="text-gray-500 text-center py-4">No comments yet</p>
              )}
              {item.comments?.map((comment: any) => (
                <div key={comment.id} className="flex space-x-3 group">
                  <img
                    src={comment.author?.picture || "/dummy-picture.jpg"}
                    alt={comment.author?.name}
                    className="w-8 h-8 rounded-full"
                  />
                  <div className="flex-1 bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{comment.author?.name}</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-400">
                          {new Date(comment.createdAt).toLocaleString()}
                        </span>
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100"
                        >
                          <FaTrash className="text-xs" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm mt-1">{comment.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldInput({
  field,
  value,
  onChange,
  allBoards = [],
}: {
  field: any;
  value: any;
  onChange: (value: any) => void;
  allBoards?: any[];
}) {
  const [localValue, setLocalValue] = useState(value);
  const [showCardSelector, setShowCardSelector] = useState(false);
  const [selectedBoardId, setSelectedBoardId] = useState("");

  const handleBlur = () => {
    if (localValue !== value) {
      onChange(localValue);
    }
  };

  const isRequiredEmpty = field.required && field.type !== "COMPUTED" &&
    (value === null || value === undefined || value === "" ||
      (Array.isArray(value) && value.length === 0));

  const fieldLabel = (
    <label className="block text-sm text-gray-600 mb-1 flex items-center gap-1">
      <HeroIconByName name={field.options?.icon} className="w-3.5 h-3.5 text-gray-400" />
      {field.label}
      {field.required && <span className="text-red-500 text-xs">*</span>}
      {isRequiredEmpty && (
        <span className="text-red-500 text-xs flex items-center gap-0.5">
          <FaExclamationTriangle className="w-3 h-3" />
          Required
        </span>
      )}
    </label>
  );

  switch (field.type) {
    case "STRING":
      return (
        <div>
          {fieldLabel}
          <input
            type="text"
            className="w-full border rounded px-3 py-2"
            value={localValue || ""}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
          />
        </div>
      );

    case "NUMBER":
      return (
        <div>
          {fieldLabel}
          <div className="flex items-center">
            {field.options?.currency && (
              <span className="border border-r-0 rounded-l px-2 py-2 bg-gray-50 text-sm text-gray-500">
                {field.options.currency}
              </span>
            )}
            <input
              type="number"
              className={`flex-1 border rounded px-3 py-2 ${
                field.options?.currency ? "rounded-l-none" : ""
              }`}
              value={localValue || ""}
              onChange={(e) => setLocalValue(e.target.value ? Number(e.target.value) : null)}
              onBlur={handleBlur}
            />
          </div>
        </div>
      );

    case "BOOLEAN":
      return (
        <div>
          <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id={`field-${field.id}`}
            checked={localValue || false}
            onChange={(e) => {
              setLocalValue(e.target.checked);
              onChange(e.target.checked);
            }}
            className="w-4 h-4"
          />
          <label htmlFor={`field-${field.id}`} className="text-sm text-gray-600 flex items-center gap-1">
            <HeroIconByName name={field.options?.icon} className="w-3.5 h-3.5 text-gray-400" />
            {field.label}
            {field.required && <span className="text-red-500 text-xs">*</span>}
            {isRequiredEmpty && (
              <span className="text-red-500 text-xs flex items-center gap-0.5">
                <FaExclamationTriangle className="w-3 h-3" />
                Required
              </span>
            )}
          </label>
          </div>
        </div>
      );

    case "DATE":
      return (
        <div>
          {fieldLabel}
          <input
            type="date"
            className="w-full border rounded px-3 py-2"
            value={localValue ? new Date(localValue).toISOString().split("T")[0] : ""}
            onChange={(e) => {
              const newValue = e.target.value ? new Date(e.target.value).toISOString() : null;
              setLocalValue(newValue);
              onChange(newValue);
            }}
          />
        </div>
      );

    case "MULTI_SELECT": {
      const multiOpts = Array.isArray(field.options) ? field.options : (field.options?.selectOptions || []);
      const selectedValues: string[] = Array.isArray(localValue) ? localValue : [];
      const toggle = (val: string) => {
        const next = selectedValues.includes(val)
          ? selectedValues.filter((v) => v !== val)
          : [...selectedValues, val];
        setLocalValue(next);
        onChange(next);
      };
      return (
        <div>
          {fieldLabel}
          <div className="flex flex-wrap gap-2 mt-1">
            {multiOpts.map((opt: any) => {
              const selected = selectedValues.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggle(opt.value)}
                  className={`px-2.5 py-1 rounded-full text-sm border transition-colors ${
                    selected
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    case "SELECT": {
      const selectOpts = Array.isArray(field.options) ? field.options : (field.options?.selectOptions || []);
      return (
        <div>
          {fieldLabel}
          <select
            className="w-full border rounded px-3 py-2"
            value={localValue || ""}
            onChange={(e) => {
              setLocalValue(e.target.value || null);
              onChange(e.target.value || null);
            }}
          >
            <option value="">Select...</option>
            {selectOpts.map((opt: any) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    case "COMPUTED":
      // Computed fields are read-only, display the computed value
      const hasError = typeof value === "object" && value?.error;
      return (
        <div>
          <label className="block text-sm text-gray-600 mb-1 flex items-center gap-1">
            <HeroIconByName name={field.options?.icon} className="w-3.5 h-3.5 text-gray-400" />
            {field.label}
            <span className="ml-1 text-xs text-gray-400">(computed)</span>
          </label>
          <div className={`w-full border rounded px-3 py-2 bg-gray-50 ${hasError ? "text-red-500" : "text-gray-700"}`}>
            {hasError ? (
              <span title={value.error}>⚠️ {value.error}</span>
            ) : value !== null && value !== undefined ? (
              typeof value === "number" ? value.toLocaleString() : String(value)
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </div>
          {field.options?.formula && (
            <p className="text-xs text-gray-400 mt-1">Formula: {field.options.formula}</p>
          )}
        </div>
      );

    case "CARD":
      // Card fields allow linking to another card
      const cardValue = value as { itemId?: string; boardId?: string; title?: string } | null;
      const selectedBoard = allBoards.find((b: any) => b.id === selectedBoardId);
      
      return (
        <div>
          <label className="block text-sm text-gray-600 mb-1 flex items-center gap-1">
            <HeroIconByName name={field.options?.icon} className="w-3.5 h-3.5 text-gray-400" />
            {field.label}
            {field.required && <span className="text-red-500 text-xs">*</span>}
            {isRequiredEmpty && (
              <span className="text-red-500 text-xs flex items-center gap-0.5">
                <FaExclamationTriangle className="w-3 h-3" />
                Required
              </span>
            )}
            <span className="ml-1 text-xs text-gray-400">(linked card)</span>
          </label>
          
          {cardValue?.title && !showCardSelector ? (
            <div className="flex items-center gap-2">
              <div className="flex-1 border rounded px-3 py-2 bg-gray-50">
                <a
                  href={`/funnel/${cardValue.boardId}?item=${cardValue.itemId}`}
                  className="text-primary hover:underline flex items-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  🔗 {cardValue.title}
                </a>
              </div>
              <button
                type="button"
                onClick={() => setShowCardSelector(true)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Change
              </button>
              <button
                type="button"
                onClick={() => onChange(null)}
                className="text-sm text-red-500 hover:text-red-700"
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <select
                className="w-full border rounded px-3 py-2"
                value={selectedBoardId}
                onChange={(e) => setSelectedBoardId(e.target.value)}
              >
                <option value="">Select a board...</option>
                {allBoards.map((board: any) => (
                  <option key={board.id} value={board.id}>
                    {board.name}
                  </option>
                ))}
              </select>
              
              {selectedBoardId && (
                <select
                  className="w-full border rounded px-3 py-2"
                  value=""
                  onChange={(e) => {
                    const selectedItem = selectedBoard?.items?.find((i: any) => i.id === e.target.value);
                    if (selectedItem) {
                      onChange({
                        itemId: selectedItem.id,
                        boardId: selectedBoardId,
                        title: selectedItem.title,
                      });
                      setShowCardSelector(false);
                      setSelectedBoardId("");
                    }
                  }}
                >
                  <option value="">Select a card...</option>
                  {selectedBoard?.items?.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </select>
              )}
              
              {cardValue?.title && (
                <button
                  type="button"
                  onClick={() => {
                    setShowCardSelector(false);
                    setSelectedBoardId("");
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      );

    default:
      return (
        <div>
          {fieldLabel}
          <input
            type="text"
            className="w-full border rounded px-3 py-2"
            value={localValue || ""}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
          />
        </div>
      );
  }
}

function formatHistoryEntry(entry: any): string {
  switch (entry.changeType) {
    case "CREATED":
      return "created this item";
    case "TITLE_UPDATED":
      return `changed title from "${entry.previousValue?.title}" to "${entry.newValue?.title}"`;
    case "STAGE_CHANGED":
      return `moved from "${entry.previousValue?.stageName}" to "${entry.newValue?.stageName}"`;
    case "FIELD_UPDATED":
      return `updated ${entry.fieldDefinition?.label || "a field"}`;
    case "USER_ADDED":
      return `added ${entry.newValue?.userName} to this item`;
    case "USER_REMOVED":
      return `removed ${entry.previousValue?.userName} from this item`;
    case "ARCHIVED":
      return "archived this item";
    case "UNARCHIVED":
      return "unarchived this item";
    case "COMMENT_ADDED":
      return "added a comment";
    default:
      return entry.changeType;
  }
}
